import { AssetContainerEventType, type IAssetContainerEvent } from './asset-container-event';
import { INovaEntity } from './base';
import type { Nullable } from './globals';
import { AssetTypeEnum } from './observation';

export const ASSET_CONTAINER_DIALOG_TYPE = 'AssetContainer';

export enum AssetContainerType {
  Trailer = 'Trailer',
  Container = 'Container',
  Oversized = 'Oversized',
  Chassis = 'Chassis',
  Reefer = 'Reefer',
  Other = 'Other'
}

export enum AssetContainerState {
  Full = 'Full',
  Empty = 'Empty',
  PartiallyLoaded = 'Partially Loaded'
}

export interface IAssetContainer extends INovaEntity {
  orgId: string;
  warehouseId: string;
  type: AssetContainerType;
  code: string;
  assetVisitId?: Nullable<string>;
  companyId?: Nullable<string>;
  notes?: Nullable<string>;
  lastLocationCheckConfirmedAt?: Nullable<Date>;
}

export enum AssetContainerYardEventType {
  Arrived = 'Arrived',
  Docked = 'Docked',
  Parked = 'Parked',
  Unassigned = 'Unassigned',
  Dropped = 'Dropped',
  Hooked = 'Hooked',
  DropAppointmentLinked = 'Drop Appointment Linked',
  DropAppointmentUnlinked = 'Drop Appointment Unlinked',
  PickupAppointmentUnlinked = 'Pickup Appointment Unlinked',
  PickupAppointmentLinked = 'Pickup Appointment Linked',
  StatusUpdated = 'Status Updated'
}

export interface IAssetContainerYardEvent {
  createDateTime: Date;
  createdBy: string;
  details?: Record<string, any>;
  eventType: AssetContainerYardEventType;
}

export function getAssetContainerStatus(
  assetContainerEvents: IAssetContainerEvent[]
): string | null {
  if (Array.isArray(assetContainerEvents) && assetContainerEvents.length > 0) {
    // Get the last event based on the createDateTime
    const lastEvent = assetContainerEvents?.reduce((prev: any, current: any) => {
      return prev.createDateTime > current.createDateTime ? prev : current;
    });

    return lastEvent?.eventType || null;
  }

  return null;
}

/**
 * Statuses an asset never returns to when a move ends: `In Transit` is the state being left, and
 * `Missing` and `Departed` are records of the asset not being in the yard's hands at all.
 */
export const STATUSES_NOT_PRESERVED_THROUGH_TRANSIT: readonly AssetContainerEventType[] =
  Object.freeze([
    AssetContainerEventType.InTransit,
    AssetContainerEventType.Missing,
    AssetContainerEventType.Departed
  ]) as readonly AssetContainerEventType[];

/**
 * The work status an asset held before it started moving — the status a finished or abandoned move
 * returns it to, and the one the UI shows while it is in transit.
 *
 * Shared because the client decides what to display and the server decides what to write, and the
 * two disagreeing would mean a trailer reading differently in Helios than in the API.
 */
export function getAssetContainerStatusBeforeTransit(
  assetContainerEvents: IAssetContainerEvent[]
): AssetContainerEventType {
  const preserved = (Array.isArray(assetContainerEvents) ? assetContainerEvents : []).filter(
    event => !STATUSES_NOT_PRESERVED_THROUGH_TRANSIT.includes(event?.eventType)
  );

  const previousStatus = getAssetContainerStatus(preserved);

  return previousStatus === null
    ? AssetContainerEventType.Arrived
    : (previousStatus as AssetContainerEventType);
}

/**
 * Returns the corresponding `AssetContainerType` for a given `AssetTypeEnum`.
 * Maps specific `assetObservation` types to their respective `assetContainer` types.
 *
 * @param {AssetTypeEnum} assetObservationType assetObservation type to map
 * @returns {AssetContainerType} corresponding assetContainer type
 */
export function getAssetObservationType(assetObservationType: AssetTypeEnum): AssetContainerType {
  switch (assetObservationType) {
    case AssetTypeEnum.Container:
      return AssetContainerType.Container;
    case AssetTypeEnum.ContainerReefer:
      return AssetContainerType.Container;
    case AssetTypeEnum.Chassis:
      return AssetContainerType.Chassis;
    default:
      return AssetContainerType.Trailer;
  }
}
